import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { IZone, IZoneSchedule } from 'models';
import {
  Building,
  ServiceInterruption,
  useFetchServiceInterruptionsQuery,
  useFetchServiceInterruptionQuery,
  useFetchUsersQuery,
  useBuildings,
  useZones,
} from 'store/apis';
import { selectCurrentUserInfo } from 'store/slices/auth.slice';
import { useAppSelector } from 'store/store.hook';
import { capitalizeFirstLetter, getBuildingLocation, sortObjectsArrayByKey } from 'utils';

dayjs.extend(customParseFormat);

type GetServiceInterruptionDetailsPayload = {
  serviceInterruption: ServiceInterruption & { id: number };
  buildings: any[];
  zones: any[];
  users: any[];
};

export type ServiceInterruptionDetails = ServiceInterruption & {
  id: number;
  building:
    | (Building & {
        fullLocation: string;
      })
    | {
        fullLocation: string;
      };
  zone: IZone;
  schedule:
    | (IZoneSchedule & {
        scheduleTimestamp: number;
        scheduleTimeString: string;
      })
    | null;
  creator: {
    name: string;
  };
  createdAtTimestamp: number;
  createdAtString: string;
  statusString: string;
};

const getServiceInterruptionDetails = ({
  serviceInterruption,
  buildings,
  zones,
  users,
}: GetServiceInterruptionDetailsPayload): ServiceInterruptionDetails | undefined => {
  const { clean_code_zone_id, clean_code_zone_cleaning_schedule_id, created_at, created_by, status } =
    serviceInterruption;
  let schedule: any;

  const zone = zones.find((zone) => {
    const _schedule = zone.schedule?.find(({ id = -1 } = {}) => id === clean_code_zone_cleaning_schedule_id);

    if (_schedule) {
      schedule = { ..._schedule };
      return true;
    }

    return zone.id === clean_code_zone_id;
  });

  if (!zone) {
    return undefined;
  }

  if (!created_at) {
    return undefined;
  }

  if (!created_by) {
    return undefined;
  }

  const building = buildings.find(({ id }) => id === zone.building_id) || null;
  const fullLocation = building ? getBuildingLocation(building as any) : '';
  const newBuilding = building ? { ...building, fullLocation } : { fullLocation };

  let scheduleDetails = null;
  if (schedule) {
    const { time: scheduleTime } = schedule;
    const scheduleTimestamp = dayjs(scheduleTime, 'HH:mm:ss').valueOf();
    const scheduleTimeString = dayjs(scheduleTime, 'HH:mm:ss').format('hh:mm A');
    scheduleDetails = { ...schedule, scheduleTimestamp, scheduleTimeString };
  }
  const createdAtTimestamp = new Date(created_at).getTime();
  const createdAtString = dayjs(createdAtTimestamp).format('MM/DD/YYYY');
  const creator = users.find(({ id }) => id === created_by);
  const statusString = capitalizeFirstLetter(status);

  return {
    ...serviceInterruption,
    building: newBuilding,
    zone,
    schedule: scheduleDetails,
    creator: creator || { name: 'No name' },
    createdAtTimestamp,
    createdAtString,
    statusString,
  };
};

export const useServiceInterruptions = () => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const {
    data: serviceInterruptions = [],
    isLoading: loading_serviceInterruptions,
    ...rest
  } = useFetchServiceInterruptionsQuery({ orgSlug });
  const { data: buildings = [], isLoading: loading_buildings } = useBuildings();
  const { data: { all: zones = [] } = {}, isLoading: loading_zones } = useZones({ buildingId: null });
  const { data: users = [], isLoading: loading_users } = useFetchUsersQuery({ orgSlug });

  const data = serviceInterruptions
    .map((serviceInterruption) => getServiceInterruptionDetails({ serviceInterruption, buildings, zones, users }))
    .filter((serviceInterruption) => !!serviceInterruption)
    .sort(sortObjectsArrayByKey('createdAtTimestamp')) as ServiceInterruptionDetails[];

  return {
    data,
    isLoading: loading_serviceInterruptions || loading_buildings || loading_zones || loading_users,
    ...rest,
  };
};

type UseServiceInterruptionProps = {
  id: number;
};

export const useServiceInterruption = ({ id }: UseServiceInterruptionProps) => {
  const { orgSlug } = useAppSelector(selectCurrentUserInfo);
  const {
    data: serviceInterruption = {},
    isLoading: loading_serviceInterruption,
    ...rest
  } = useFetchServiceInterruptionQuery({ orgSlug, id });
  const { data: buildings = [], isLoading: loading_buildings } = useBuildings();
  const { data: { all: zones = [] } = {}, isLoading: loading_zones } = useZones({ buildingId: null });
  const { data: users = [], isLoading: loading_users } = useFetchUsersQuery({ orgSlug });

  const data = getServiceInterruptionDetails({ serviceInterruption, buildings, zones, users });

  return {
    data,
    isLoading: loading_serviceInterruption || loading_buildings || loading_zones || loading_users,
    ...rest,
  };
};
